import { getDiscountPriceWithDefault, getVariantWeightPrice } from "./product";
import {
  DELIVERY_OPTIONS,
  FREE_DELIVERY_FROM_PRICE,
  MINIMUM_ORDER_AMOUNT,
  SEND_COST,
  getDeliveryTimeSlotSurcharge,
} from "../constants/DeliveryConstants";

/**
 * Zitten er producten in de winkelwagen die al in de aanbieding zijn? Een kortingscode met
 * `applyOnlyToNonDiscountedProducts` slaat die producten over (zie de skip hieronder in
 * calculateDiscountPercentageTotalAmount), waardoor de korting lager uitvalt dan het
 * percentage van de code doet vermoeden. Wordt gebruikt om dat aan de klant uit te leggen -
 * zie DiscountCodeField.
 */
export const cartHasDiscountedProducts = (cartItems) =>
  !!cartItems?.some((product) => product.discount > 0);

export const calculateDiscountPercentageTotalAmount = (
  cartItems,
  discountCode,
) => {
  let totalDiscountAmount = 0;
  cartItems.forEach((product) => {
    // Skip products with discount if code only applies to non-discounted products
    if (discountCode.applyOnlyToNonDiscountedProducts && product.discount > 0) {
      return;
    }

    let productPrice = getVariantWeightPrice(product);
    let discountPrice = getDiscountPriceWithDefault(
      productPrice,
      product.discount,
    );
    let discountAmountWithCode = calculateDiscountWithPercentage(
      discountPrice,
      discountCode.discountPercentage,
    );
    totalDiscountAmount += discountAmountWithCode * product.quantity;
  });
  return roundPriceTwoDecimals(totalDiscountAmount);
};

export const calculatePickupDiscountAmount = (cartItems) => {
  let total = 0;
  cartItems.forEach((product) => {
    if (product.discount > 0) return;
    const productPrice = getVariantWeightPrice(product);
    total += productPrice * product.quantity;
  });
  return roundPriceTwoDecimals(total * 0.03);
};

export const calculateDiscountAmountTotalAmount = (cartItems, discountCode) => {
  return discountCode.discountAmount;
};

export const calculateDiscountWithPercentage = (price, percentage) => {
  return (price * percentage) / 100;
};

export const roundPriceTwoDecimals = (price) => {
  return Math.round(price * 100) / 100;
};

/**
 * De verzendkosten in euro's, of `null` zolang de postcode onbekend is.
 *
 * Een regel voor iedereen: onder de drempel kost bezorgen `SEND_COST`, daarboven
 * niets. De postcode en het land bepalen de PRIJS niet meer - dat was tot
 * september 2026 wel zo, met vier staffels naast elkaar (Enkhuizen/Stede Broec
 * 3,95, overig West-Friesland 4,95, landelijk 8,95/5,95/gratis vanaf 150, en
 * Belgie een vaste 8,95 die nooit gratis werd).
 *
 * Daarom is er ook geen postcode meer nodig om het bedrag te noemen: de
 * winkelwagen toont het meteen, ook als de shop het adres nog niet kent
 * (besluit 2026-10-10). Of we op dat adres bezorgen (Waddeneilanden, Wallonie)
 * is een andere vraag, en die stelt de checkout.
 *
 * Afhalen kost NOOIT verzendkosten. Dat hoort in deze functie en niet bij de
 * aanroeper: zolang de bezorgmethode buiten de berekening bleef, toonde de
 * winkelwagen een bedrag dat in de checkout weer verdween.
 *
 * @param {number} totalItemsPrice Productsubtotaal, exclusief cadeaubonnen.
 * @param {number} totalGiftcardsPrice Cadeaubonsubtotaal. Telt niet mee voor de
 *   drempel; staat er nog in omdat alle aanroepers hem doorgeven en het bedrag
 *   de betekenis van `totalItemsPrice === 0` leesbaar houdt.
 * @param {{zip: string|number, country: string}} deliveryValues Het bezorgadres.
 *   Bepaalt het bedrag niet meer; de parameter blijft omdat elke aanroeper hem
 *   doorgeeft.
 * @param {string} deliveryMethod `DELIVERY_OPTIONS.SEND` of `.PICKUP`. De
 *   winkelwagen kent de keuze nog niet - daar wordt bewust `SEND` doorgegeven,
 *   met een tekst erbij dat afhalen gratis is.
 * @param {string|null} timeSlot Het gekozen levervenster als contractlabel
 *   (`DELIVERY_TIME_SLOTS[].label`). De winkelwagen kent dat nog niet en geeft
 *   niets mee; dan geldt het basistarief, zoals de afhaal-notitie daar ook al
 *   werkt. Een onbekend label kost nooit toeslag.
 */
export const calculateShippingCost = (
  totalItemsPrice,
  totalGiftcardsPrice,
  deliveryValues,
  deliveryMethod = DELIVERY_OPTIONS.SEND,
  timeSlot = null,
) => {
  // Afhalen kost nooit verzendkosten.
  if (deliveryMethod === DELIVERY_OPTIONS.PICKUP) return 0;

  // Alleen cadeaubonnen in de wagen: er valt niets te verzenden.
  if (totalItemsPrice === 0) return 0;

  /**
   * De toeslag op de twee smalle vensters (08:00-12:00 en 12:00-17:00).
   *
   * Hij komt BOVENOP het bezorgtarief en vervalt NIET boven de gratis-drempel.
   * Dat is geen slordigheid maar de afspraak: gratis bezorging is een cadeau op
   * het BEZORGEN, en een smaller venster is een premium op de bezorging zelf -
   * SooCool rekent ons die EUR 1,00 exclusief btw ook bij een order van EUR 300.
   *
   * Gevolg, en dat moet de samenvatting eerlijk tonen: een klant boven EUR 150
   * ziet "gratis bezorging" staan met EUR 1,25 ernaast. Zie
   * `formatShippingCost` in `useCheckoutState`, waar die twee als een regel met
   * een toelichting getoond worden in plaats van als een raadsel.
   */
  const surcharge = getDeliveryTimeSlotSurcharge(timeSlot);

  if (totalItemsPrice >= FREE_DELIVERY_FROM_PRICE) {
    return roundPriceTwoDecimals(surcharge);
  }

  return roundPriceTwoDecimals(SEND_COST + surcharge);
};

/**
 * Hoeveel er nog bij moet om te mogen laten BEZORGEN, gemeten over het
 * productsubtotaal. 0 betekent: het minimum is gehaald (of de wagen is leeg).
 *
 * Bestaat zodat de winkelwagen dezelfde grens kan tonen als de checkout. Tot nu
 * toe kende alleen de checkout het minimum, dus een wagen van EUR 40 kreeg
 * netjes "Verzendkosten EUR 9,95" te zien en liep pas bij het afrekenen vast.
 * Cadeaubonnen tellen niet mee - vandaar het productsubtotaal en niet het
 * wagentotaal.
 */
export const calculateRemainingForMinimumOrder = (totalItemsPrice) => {
  if (!(totalItemsPrice > 0)) return 0;
  const remaining = MINIMUM_ORDER_AMOUNT - totalItemsPrice;
  return remaining > 0 ? roundPriceTwoDecimals(remaining) : 0;
};

/**
 * Hoeveel er nog bij moet voor gratis bezorging. 0 betekent: geen boodschap
 * tonen - al gratis, of het adres is nog onbekend.
 *
 * Belgie staat hier niet meer buiten. De oude versie sloot dat land
 * onvoorwaardelijk uit ("Belgium never gets free shipping"), omdat het daar ook
 * echt nooit gratis werd; nu haalt Belgie dezelfde drempel als Nederland.
 */
export const calculateRemainingForFreeShipping = (
  shippingCost,
  totalItemsPrice,
) => {
  if (shippingCost == null || shippingCost === 0) return 0;
  if (totalItemsPrice <= 0) return 0;
  const remaining = FREE_DELIVERY_FROM_PRICE - totalItemsPrice;
  return remaining > 0 ? roundPriceTwoDecimals(remaining) : 0;
};
